import { getStoredReturnUrl } from "common/app";

/**
 * 실행 환경 판별
 * - ReactNativeWebView: nest 네이티브 앱 WebView
 * - window.opener: moi 등 외부 웹앱에서 popup으로 열린 경우
 * - none: 직접 URL 이동으로 열린 경우 (returnUrl 리다이렉트 방식)
 */
export const getClientMode = (): "native" | "web" | "none" => {
    if (window.ReactNativeWebView) return "native";
    if (window.opener) return "web";
    return "none";
};

/**
 * opener origin 추출 (보안용 targetOrigin)
 * URL 쿼리 또는 sessionStorage에서 가져옴 (SPA 내부 이동 시 유지)
 */
const getOpenerOrigin = (): string => {
    const params = new URLSearchParams(window.location.search);
    return params.get("origin") || sessionStorage.getItem("sso_origin") || "*";
};

/**
 * 토큰을 hash fragment에 담아 returnUrl로 리다이렉트
 * hash fragment는 서버로 전송되지 않아 보안상 안전 (OAuth 2.0 Implicit Flow 방식)
 */
const redirectWithTokens = (returnUrl: string, data: any) => {
    const hashParams = new URLSearchParams();
    hashParams.set("access_token", data.access_token || "");
    hashParams.set("refresh_token", data.refresh_token || "");
    hashParams.set("user_id", data.user_id || "");
    // 사용 후 정리
    sessionStorage.removeItem("sso_returnUrl");
    sessionStorage.removeItem("sso_serviceId");
    sessionStorage.removeItem("sso_origin");
    window.location.href = `${returnUrl}#${hashParams.toString()}`;
};

export const requestApp = (type: string, data?: any) => {
    const mode = getClientMode();
    const payload = { type, data: data || {} };

    // SEND_TOKEN 시 returnUrl이 있으면 어떤 모드든 hash fragment 리다이렉트 우선
    // (moi 등 외부 앱의 WebView에서 SSO로 직접 이동한 경우)
    if (type === WEB_TO_APP.SEND_TOKEN) {
        const returnUrl = getStoredReturnUrl();
        if (returnUrl && data) {
            // 네이티브인 경우 앱에도 토큰 전달 (네이티브 저장소 활용)
            if (mode === "native") {
                window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
            }
            redirectWithTokens(returnUrl, data);
            return;
        }
    }

    if (mode === "native") {
        // nest 네이티브 앱: ReactNativeWebView로 전달 (SSO가 직접 열린 경우)
        window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
    } else if (mode === "web") {
        // moi 웹앱: opener(부모 창)로 postMessage 전달
        const origin = getOpenerOrigin();
        window.opener.postMessage(payload, origin);

        // 토큰 전송 시 팝업 자동 닫기
        if (type === WEB_TO_APP.SEND_TOKEN) {
            window.close();
        }
    }
}

export const APP_TO_WEB = {
    INSET_INFO: "INSET_INFO",
    DEVICE_INFO: "DEVICE_INFO",
    SOCIAL_FAILED: "SOCIAL_FAILED",
    /** 안드로이드 device back — 네이티브가 webview.goBack() 대신 이 메시지로 위임.
     *   웹의 현재 활성 페이지(useBackInterceptor 로 등록된) 가 자체 처리. */
    PRESS_BACK: "PRESS_BACK",
}

export const WEB_TO_APP = {
    LOGS: "LOGS",
    MSG_READY : "MSG_READY",
    SEND_TOKEN: "SEND_TOKEN",
    SOCIAL_SIGN_IN: "SOCIAL_SIGN_IN",
    AUTO_SIGN_IN: "AUTO_SIGN_IN",
    /** device back 가로채기 모드 on/off — 페이지가 마운트/언마운트 시 토글.
     *   on 인 동안 네이티브는 device back 을 PRESS_BACK 메시지로 위임하고, off 면 기본 동작(webview.goBack/doubleBackExit) 유지. */
    SET_BACK_INTERCEPT: "SET_BACK_INTERCEPT",
    /** 네이티브 토스트 표시 요청 — data: { title: string }.
     *   webview 안 SSO 페이지에서 dead-end(가입 완료 등) device back 차단 안내 등에 사용. */
    SHOW_TOAST: "SHOW_TOAST",
    WITHDRAW_CLEANUP: "WITHDRAW_CLEANUP", // 회원탈퇴 API 성공 직후 — 네이티브가 즉시 토큰/MMKV 정리 (모달은 그대로). device back race 방지용 안전망.
    WITHDRAW_DONE: "WITHDRAW_DONE",   // 회원탈퇴 완료 — 네이티브가 토큰 정리(idempotent) + 모달 닫기 + Main 이동
    WITHDRAW_CLOSE: "WITHDRAW_CLOSE", // 회원탈퇴 취소/종료 — 네이티브가 모달만 닫음
}

// 네이티브에서 받은 이벤트 가공
export const msgData = (event: MessageEvent) => {
    const originData = event?.data;
    if (!originData) return { type: undefined, data: undefined };

    let response;
    try {
        response = JSON.parse(originData || '{}');
    } catch (error) {
        return { type: undefined, data: undefined };
    }

    const type = (response?.type as string)?.toUpperCase();
    let data = response.data;
    try {
        data = JSON.parse(response.data);
    } catch (error) {
        data = response.data;
    }

    return { type, data };
}
